import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Certification,
  CertificationType,
  PaginatedResponse,
  CreateCertificationPayload,
} from '@/lib/types';

export interface CertificationFilters {
  page?: number;
  page_size?: number;
  cert_type?: CertificationType | '';
  asset?: number | '';
  is_expired?: boolean | '';
  ordering?: string;
}

export interface CertificationWithDetails extends Certification {
  asset_name?: string;
  asset_serial_number?: string;
}

export function useCertifications(filters: CertificationFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.certifications.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<CertificationWithDetails>> => {
      const response = await apiClient.get<PaginatedResponse<CertificationWithDetails>>(
        '/api/v1/certifications/',
        { params: cleanFilters }
      );
      return response.data;
    },
  });
}

export function useCreateCertification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCertificationPayload): Promise<Certification> => {
      const response = await apiClient.post<Certification>('/api/v1/certifications/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.certifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
    },
  });
}

export function useUpdateCertification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateCertificationPayload>;
    }): Promise<Certification> => {
      const response = await apiClient.patch<Certification>(
        `/api/v1/certifications/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.certifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.certifications.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
    },
  });
}

export function useDeleteCertification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/certifications/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.certifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
    },
  });
}
